import { computePaymentMethodDiscount } from '@/lib/payments/paymentMethodDiscount';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Check, Copy, AlertTriangle, ArrowRightLeft, ScanLine } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import { CurrencyInput } from '@/components/molecules/CurrencyInput';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { useRecordPayment } from '@/hooks/invoices/useRecordPayment';
import { useChargeSavedInvoicePayment } from '@/hooks/invoices/useChargeSavedInvoicePayment';
import { useSendInvoice } from '@/hooks/invoices/useSendInvoice';
import { useJobChecklistReports } from '@/hooks/serviceRequests/useChecklistInstances';
import {
  useContactPaymentMethods,
  useDeleteContactPaymentMethod,
} from '@/hooks/contacts/useContactPaymentMethods';
import type { ContactPaymentMethod } from '@/types/contact';
import { useDebounce } from '@/hooks/useDebounce';
import { useCardServiceFeeQuote } from '@/hooks/payments/useCardServiceFeeQuote';
import { PaymentMethodHealthBadge } from '@/components/organisms/contacts/PaymentMethodHealthBadge';
import {
  formatSavedMethodOptionLabel,
  isAcceptedBankDebit,
  isBankPaymentMethod,
  isSavedMethodChargeable,
  isWrittenOffDeclineState,
  savedMethodUnchargeableReason,
  unchargeableSavedMethodsMessage,
  type SavedMethodChargeSettings,
} from '@/lib/payments/savedPaymentMethods';
import {
  chargeRefusedBeforeCharging,
  classifyNewMethodChargeFailure,
  describeReplayedCharge,
  replayedEarlierMethod,
  newMethodValue,
  parseNewMethodRail,
} from '@/lib/payments/newMethodCharge';
import { ChargeServiceFeePreview } from '@/components/organisms/payments/ChargeServiceFeePreview';
import { AchAuthorizationFields } from '@/components/organisms/payments/AchAuthorizationFields';
import { useAchAuthorization } from '@/hooks/payments/useAchAuthorization';
import {
  NewPaymentMethodCharge,
  railAvailability,
  type StaffChargeAuthorization,
} from '@/components/organisms/payments/NewPaymentMethodCharge';
import { PayabliCheckScanForm } from '@/components/organisms/payments/PayabliCheckScanForm';
import { useChequeScanAvailability } from '@/hooks/payments/useChequeScan';
import { usePayabliClientConfig, usePaymentSettings } from '@/hooks/settings/usePaymentSettings';
import { createUuid } from '@notifi/uuid/browser';
import { useContactSummary } from '@/hooks/contacts/useContactSummary';
import { useApplyCredit } from '@/hooks/invoices/useApplyCredit';
import { useContact } from '@/hooks/contacts/useContact';
import { getContactDisplayName } from '@/lib/contacts';
import { useCompanySettings } from '@/hooks/settings/useCompanySettings';
import { useBillingSettings } from '@/hooks/settings/billing/useBillingSettings';
import { apiPost, operationsClient } from '@/lib/api';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { invoiceKeys } from '@/lib/queryKeys';
import { computeEffectiveInvoiceStatus } from '@/lib/invoice-status';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import { billToRecipientEmail, invoiceBillToRecipientPhone } from '@/lib/invoices/billTo';
import { trackAction } from '@/lib/monitoring';
import { ApiError } from '@/lib/api/errors';
import { toastApiError } from '@/lib/api/error-toast';
import { validationToastMessage } from '@/hooks/useApiMutation';
import type { Invoice, PaymentMethod, GeneratePaymentLinkResponse } from '@/types/invoice';

/**
 * Shown for a new-method charge whose outcome the server never definitively
 * answered — a lost response, a still-processing idempotency lease, a rate
 * limit, or a same-key collision. Any of these could mean the charge
 * actually went through; the mapped decline message would be a lie in that
 * case, and removing a one-time method here could orphan a real charge from
 * the very method that made it. Mirrors the contact dialog's own copy.
 */
const AMBIGUOUS_NEW_METHOD_CHARGE_MESSAGE =
  "We couldn't confirm whether this charge went through. Check this invoice's payments before trying again.";

// CARD and ACH used to be here as "record it happened elsewhere" entries.
// Both are removed (BR-77): a card taken on an outside terminal is now
// recorded as Other, and charging a bank account goes through the new
// "Bank account (ACH)" collector option below instead of being recorded
// blind. Cash/Check/Other are unaffected — those are still money taken
// outside this system, nothing to charge.
const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Check' },
  { value: 'OTHER', label: 'Other' },
];

/** The manual method picker's value: a real recorded `PaymentMethod`, or the
 * `new:card` / `new:ach` pseudo-choice that renders `NewPaymentMethodCharge`
 * instead of recording anything blind. */
type ManualMethodChoice = PaymentMethod | 'new:card' | 'new:ach';

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

// ─── SendDialog (moved from route) ──────────────────────────────────────────

interface SendDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice;
}

export function SendDialog({ open, onClose, invoice }: SendDialogProps) {
  // `both` is implemented end to end already — `useSendInvoice` fans it out
  // into two legs on an issued invoice and `deliverInvoiceCommunication` fans
  // it out on the issue path — so this toggle was the only thing withholding
  // it. Mirrors the collect-payment dialog's "Email and SMS receipt".
  const [channel, setChannel] = useState<'email' | 'sms' | 'both'>('email');
  const [subject, setSubject] = useState('');
  const [selectedChecklistReportIds, setSelectedChecklistReportIds] = useState<string[]>([]);
  const sendInvoice = useSendInvoice();
  const { data: checklistReports = [] } = useJobChecklistReports(
    open && invoice.jobId ? invoice.jobId : null,
  );
  // Use the tenant's real business name in the subject (matching the invoice
  // PDF + the estimate email), not a hardcoded "Your Company" placeholder.
  const { data: companySettings } = useCompanySettings();
  const companyName = companySettings?.name?.trim() || 'your company';
  const ownerContactId = invoice.contact?.id ?? invoice.contactId;
  const billToContactId = invoice.billToContact?.contactId;
  const isDistinctBillTo = Boolean(billToContactId && billToContactId !== ownerContactId);
  const { data: liveBillToContact } = useContact(billToContactId ?? '', {
    enabled: open && isDistinctBillTo && !invoice.billToContact?.phone,
  });

  // Recipient is resolved from the contact (or the bill-to party) and is not user-editable —
  // the invoice always goes to the customer on file, never an ad-hoc address/number.
  const recipientEmail = billToRecipientEmail(invoice.billToContact, invoice.contact?.primaryEmail);
  const recipientPhone = invoiceBillToRecipientPhone(invoice, liveBillToContact?.primaryPhone);

  const defaultSubject = `Invoice ${invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : ''} from ${companyName}`;
  // Tracks the last auto-applied default so we can refresh it when the company
  // name resolves without clobbering a manual edit.
  const appliedDefaultRef = useRef('');

  useEffect(() => {
    if (!open) return;
    setSubject(defaultSubject);
    appliedDefaultRef.current = defaultSubject;
    setSelectedChecklistReportIds(invoice.checklistReportIds ?? []);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // If `useCompanySettings()` resolves after the dialog is already open (cold
  // load), the default subject changes from the "your company" fallback to the
  // real name — re-sync the field, but only while it still holds the default so
  // a manual edit is never overwritten.
  useEffect(() => {
    if (!open) return;
    setSubject((current) => (current === appliedDefaultRef.current ? defaultSubject : current));
    appliedDefaultRef.current = defaultSubject;
  }, [open, defaultSubject]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleChecklistReport(reportId: string) {
    setSelectedChecklistReportIds((current) =>
      current.includes(reportId) ? current.filter((id) => id !== reportId) : [...current, reportId],
    );
  }

  // An issued invoice cannot go back through `/send`; see useSendInvoice.
  const issued = computeEffectiveInvoiceStatus(invoice) !== 'draft';

  function handleSend() {
    sendInvoice.mutate(
      {
        id: invoice.id,
        issued,
        communicationOption: channel,
        checklistReportIds: selectedChecklistReportIds,
        // The subject and the resolved recipient only belong to a send that
        // has an email leg; an SMS-only send must not carry them.
        ...(channel === 'sms' ? {} : { subject, customerEmail: recipientEmail || undefined }),
      },
      {
        onSuccess: () => {
          trackAction('invoice_sent', { invoiceId: invoice.id, channel });
          onClose();
        },
      },
    );
  }

  // Every leg the operator picked has to be sendable. `both` with no phone on
  // file would silently go out as an email-only send and report success.
  const canSendEmail = subject.trim().length > 0 && recipientEmail.length > 0;
  const canSendSms = recipientPhone.length > 0;
  const canSend =
    channel === 'email'
      ? canSendEmail
      : channel === 'sms'
        ? canSendSms
        : canSendEmail && canSendSms;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Invoice</DialogTitle>
          <DialogDescription>Choose how to send this invoice to your customer.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            variant={channel === 'email' ? 'default' : 'outline'}
            onClick={() => setChannel('email')}
            className="flex-1"
          >
            Email
          </Button>
          <Button
            size="sm"
            variant={channel === 'sms' ? 'default' : 'outline'}
            onClick={() => setChannel('sms')}
            className="flex-1"
          >
            SMS
          </Button>
          <Button
            size="sm"
            variant={channel === 'both' ? 'default' : 'outline'}
            onClick={() => setChannel('both')}
            className="flex-1"
          >
            Email &amp; SMS
          </Button>
        </div>

        <div className="space-y-3 py-1">
          {checklistReports.length > 0 && (
            <div className="space-y-2 rounded-md border bg-muted/20 p-3">
              <p className="text-sm font-medium">Checklist reports</p>
              <div className="space-y-1.5">
                {checklistReports.map((report) => (
                  <label
                    key={report.id}
                    className="flex items-center gap-2 rounded-md bg-background px-2 py-1.5 text-sm"
                  >
                    <Checkbox
                      checked={selectedChecklistReportIds.includes(report.id)}
                      onCheckedChange={() => toggleChecklistReport(report.id)}
                    />
                    <span className="min-w-0 flex-1 truncate">{report.templateName}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Each leg shows its own recipient — on `both` the operator has to
              be able to see BOTH before sending, since a missing one blocks
              the send rather than quietly dropping that leg. */}
          {channel !== 'sms' ? (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Recipient Email</label>
                <div className="flex h-9 items-center rounded-md border bg-muted/40 px-3 text-sm">
                  {recipientEmail || (
                    <span className="text-muted-foreground">No email on file for this contact</span>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Subject</label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
            </>
          ) : null}
          {channel !== 'email' ? (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Recipient Number</label>
              <div className="flex h-9 items-center rounded-md border bg-muted/40 px-3 text-sm">
                {recipientPhone ? (
                  formatPhoneDisplay(recipientPhone)
                ) : (
                  <span className="text-muted-foreground">No phone on file for this contact</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                The invoice is texted to the contact&apos;s phone number on file.
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sendInvoice.isPending || !canSend}>
            {sendInvoice.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Send {channel === 'email' ? 'Email' : channel === 'sms' ? 'SMS' : 'Email & SMS'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── RecordPaymentDialog (moved from route) ──────────────────────────────

type PayerPaymentMethod = ContactPaymentMethod & { payerLabel: string | null };

/**
 * Which saved method the dialog opens on: the customer's own default, else the
 * customer's first card, and only then the bill-to contact's (default first).
 * `isDefault` is per contact, so a merged list can carry two defaults — the
 * customer's own must win, in either order the two lists arrive.
 *
 * Callers pass the chargeable subset only. A contact's default card may be a
 * leftover Stripe token on a Payabli-only tenant; opening on it hands the
 * operator a pre-selected choice whose only outcome is a 400. With none
 * chargeable the list is empty, this returns '', and the picker explains itself
 * instead of showing a selection that cannot be submitted.
 *
 * A method the processor has written off (HARD_DECLINED / INVALID) is never
 * opened on either. It stays listed and pickable — the server allows an
 * operator to charge it — but one click on a pre-selected closed account is
 * not a choice anyone made. With only written-off methods this returns '' and
 * the operator picks explicitly.
 */
function preferredSavedMethodId(methods: PayerPaymentMethod[]): string {
  const usable = methods.filter((item) => !isWrittenOffDeclineState(item.declineState));
  const own = usable.filter((item) => item.payerLabel === null);
  const billTo = usable.filter((item) => item.payerLabel !== null);
  return (
    own.find((item) => item.isDefault)?.id ??
    own[0]?.id ??
    billTo.find((item) => item.isDefault)?.id ??
    billTo[0]?.id ??
    ''
  );
}

interface RecordPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  balanceDue: number;
  /**
   * False when the caller is masked out of PAYMENT_INFO (or PRICING) and the
   * server stripped `balanceDue` — a technician collecting cash at the door
   * may take the money without being shown what is owed. `balanceDue` is then a
   * meaningless 0, so the dialog must not prefill it, validate against it, or
   * print it as the customer's balance.
   */
  balanceDueKnown?: boolean;
  contactId?: string;
  /**
   * Whether the caller holds `payments:edit`.
   *
   * Recording money manually is a `payments:create` act, but the dialog's two
   * other modes both move money the customer already gave us, and both require
   * `payments:edit` server-side:
   *   - charge a saved method → POST /invoices/:id/payments/gateway
   *   - apply credit          → POST /v2/billing/payments/apply-credit
   *
   * office_manager, accountant and service_technician hold create but not edit,
   * so offering either control to them produces a guaranteed 403. One flag
   * rather than one per control, deliberately: gating them separately is what
   * let the credit path keep offering itself after the saved-method path was
   * fixed.
   */
  canEditPayments?: boolean;
  /**
   * F5 — the invoice's bill-to contact when it is a DIFFERENT real contact
   * (a property manager, a parent company). Their saved methods are offered
   * alongside the customer's: the party the invoice says pays must be
   * chargeable from here. Null / same as `contactId` → nothing extra.
   */
  billToContactId?: string | null;
  billToContactName?: string | null;
  /** Display name for `contactId`, used only by the "charge a new card/bank
   * account" collector's ACH authorization text. */
  contactName?: string;
  /** `contactId`'s email, used only so the new-method collector's ACH
   * authorization copy can tell the operator when there is nowhere to send
   * it. Absent (not the invoice's own email) → treated as unknown, not blank. */
  contactEmail?: string | null;
  /**
   * Why this invoice is not collectable right now — "$500.00 pending ACH" —
   * or null. Money already in flight that covers the balance; the caller
   * resolves it with `invoiceCollectionBlockedReason`, which is null for any
   * actor the server withholds `settlingPayment` from.
   *
   * The menu that opens this dialog disables its own control, so this is the
   * case where the debit landed while the dialog was already open. Passed as
   * the resolved string rather than the payment: the dialog needs to say it,
   * not to reason about rails.
   */
  clearingReason?: string | null;
}

export function RecordPaymentDialog({
  open,
  onClose,
  invoiceId,
  balanceDue,
  balanceDueKnown = true,
  contactId,
  canEditPayments = true,
  billToContactId = null,
  billToContactName = null,
  contactName = '',
  contactEmail = null,
  clearingReason = null,
}: RecordPaymentDialogProps) {
  const [amount, setAmount] = useState<number | ''>(balanceDueKnown ? balanceDue : '');
  const [tip, setTip] = useState<number | ''>('');
  const [method, setMethod] = useState<ManualMethodChoice>('CASH');
  const [transactionId, setTransactionId] = useState('');
  const [mode, setMode] = useState<'manual' | 'saved' | 'credit' | 'scan'>('manual');
  // A scanned check is being deposited: the dialog must not close, and the
  // form must not be swapped out, while the component holds the check.
  const [scanSubmitting, setScanSubmitting] = useState(false);
  // The scan form told Payabli to debit the check and the server has not yet
  // proved it did not. Like a saved-method charge that went out, it locks the
  // dialog against every other way of collecting: that debit may still land.
  const [scanDebitDispatched, setScanDebitDispatched] = useState(false);
  const scanLocked = scanSubmitting || scanDebitDispatched;
  const [savedPaymentMethodId, setSavedPaymentMethodId] = useState('');
  const [overpaymentConfirmOpen, setOverpaymentConfirmOpen] = useState(false);
  const paymentSession = useRef<string | null>(null);
  // Whether this session's mode is settled: chosen by the dialog once saved
  // methods and tenant settings have both answered, or by the operator acting
  // first. Once settled, data arriving never moves the operator.
  const modeSettled = useRef(false);
  // Set the moment a saved-method charge is handed to the mutation. The
  // mutation's own `isPending` reaches this component a tick later (react-query
  // notifies on a timer), so a second click inside that tick would otherwise
  // send a second charge.
  const savedChargeSubmitting = useRef(false);
  /**
   * A new-method charge attempt is only ever safe to REPLAY (same
   * idempotency key) when the previous attempt for the same rail + amount
   * got no definitive answer from the server. Anything else (success, or a
   * decline the server actually processed) means the NEXT attempt is a
   * deliberate new charge and must mint a fresh key. Mirrors the contact
   * dialog's `newMethodChargeAttemptRef`.
   */
  const newMethodChargeAttemptRef = useRef<{
    key: string;
    rail: 'card' | 'ach';
    amount: number;
    /** Every method sent under `key` — what a replayed answer is matched to. */
    providerIds: ReadonlySet<string>;
  } | null>(null);
  // Every method this dialog has sent a new-method charge for. A method sent
  // once may be the one an unresolved charge took, so it is never removed on
  // an answer that is only proof about a DIFFERENT method.
  const newMethodSentIdsRef = useRef(new Set<string>());
  // A new-method charge between hand-off and answer. The ref is the
  // same-tick guard `handleRecord` reads; the state is what re-renders the
  // dialog locked. `chargeNewMethodPayment.isPending` alone arrives a tick
  // late (react-query notifies on a timer).
  const newMethodChargeSubmitting = useRef(false);
  const [newMethodChargeSubmittingState, setNewMethodChargeSubmittingState] = useState(false);
  // A new-method charge the server answered without confirming (a card that
  // came back PENDING). Like a saved-method charge in the same state, it
  // keeps the dialog open on its status copy and locked.
  const [newMethodChargeUnconfirmed, setNewMethodChargeUnconfirmed] = useState(false);
  const recordPayment = useRecordPayment();
  // Tenant tipping switch. Default on while the query is in flight so the
  // field does not flicker away for the tenants who do take tips.
  const billingSettingsQuery = useBillingSettings();
  const tipsEnabled = billingSettingsQuery.data?.paymentMethods.tipsEnabled !== false;
  // Check scanning: a Payabli tenant that switched it on. Whether Payabli has
  // enabled it on the paypoint is the server's to say (`availability`), and
  // until it says yes the option is shown disabled with its reason.
  const scanSettingOn =
    billingSettingsQuery.data?.paymentMethods.primaryProcessor === 'payabli' &&
    billingSettingsQuery.data?.paymentMethods.acceptChequeScan === true;
  // Read once per open, not again on window focus: the answer is a live
  // Payabli call that can blink "unavailable", and the form must not be torn
  // out from under a deposit because it did.
  const chequeScanAvailability = useChequeScanAvailability({
    enabled: open && scanSettingOn,
    refetchOnWindowFocus: false,
  });
  // One read of the Payabli client config serves both the scan form and the
  // new-method collector's rail gating below.
  const paymentSettingsQuery = usePaymentSettings();
  const isPayabliProcessor = paymentSettingsQuery.data?.primaryProcessor === 'payabli';
  const payabliConfigQuery = usePayabliClientConfig({
    enabled: isPayabliProcessor || (open && scanSettingOn),
  });
  const payabliConfig = payabliConfigQuery.data;
  const scanConfig =
    payabliConfig?.publicToken && payabliConfig.entryPoint
      ? {
          publicToken: payabliConfig.publicToken,
          entryPoint: payabliConfig.entryPoint,
          mode: payabliConfig.mode,
        }
      : null;
  const scanAvailable = chequeScanAvailability.data?.available === true && scanConfig !== null;
  // The config the scan form was mounted with, kept while a deposit is in
  // flight whatever a refetch says.
  const lastScanConfig = useRef(scanConfig);
  if (scanConfig) lastScanConfig.current = scanConfig;
  const scanFormConfig =
    scanAvailable && scanConfig ? scanConfig : scanLocked ? lastScanConfig.current : null;
  const scanUnavailableReason = !scanSettingOn
    ? null
    : chequeScanAvailability.isLoading || payabliConfigQuery.isLoading
      ? null
      : chequeScanAvailability.data && !chequeScanAvailability.data.available
        ? (chequeScanAvailability.data.reason ?? 'Check scanning is unavailable')
        : !chequeScanAvailability.data
          ? 'Could not check whether check scanning is available'
          : !scanConfig
            ? 'Payabli is not connected for this business'
            : null;
  const chargeSavedPayment = useChargeSavedInvoicePayment();
  // A separate mutation instance (same endpoint) so the hook's own generic
  // result toast can be suppressed here without touching the pre-existing
  // "Charge Saved Method" flow above, which still relies on it.
  // `chargeNewMethod` shows its own exact-copy toast instead, and classifies
  // failures itself (a definitive decline vs. an ambiguous "we don't know if
  // it went through" outcome) rather than stacking this mutation's generic
  // raw-error toast on top of that.
  const chargeNewMethodPayment = useChargeSavedInvoicePayment({
    showSuccessToast: false,
    showErrorToast: false,
  });
  // A hard-declined one-time method is already off the contact server-side.
  const deletePaymentMethod = useDeleteContactPaymentMethod(contactId ?? '', {
    notFoundIsRemoved: true,
  });
  // Which rails the manual method picker's "Card" / "Bank account (ACH)"
  // choices offer — the same resolution `NewPaymentMethodCharge` uses
  // internally, so the picker never lists a rail the collector itself would
  // refuse to mint.
  const newMethodRailAvailability = useMemo(
    () => railAvailability(paymentSettingsQuery.data, payabliConfigQuery.data),
    [paymentSettingsQuery.data, payabliConfigQuery.data],
  );
  const applyCredit = useApplyCredit(contactId ?? '');
  const paymentMethodsQuery = useContactPaymentMethods(contactId ?? '');
  const billToPayerId = billToContactId && billToContactId !== contactId ? billToContactId : null;
  const billToMethodsQuery = useContactPaymentMethods(billToPayerId ?? '', {
    enabled: Boolean(billToPayerId),
  });
  const contactSummaryQuery = useContactSummary(contactId ?? '');
  // The customer's own methods first (unchanged), then the bill-to contact's,
  // each of those labelled with who holds it so the operator charges the
  // party they mean to.
  const savedMethods = useMemo<PayerPaymentMethod[]>(() => {
    const own: PayerPaymentMethod[] = (paymentMethodsQuery.data ?? []).map((method) => ({
      ...method,
      payerLabel: null,
    }));
    if (!billToPayerId) return own;
    const billTo: PayerPaymentMethod[] = (billToMethodsQuery.data ?? [])
      .filter((method) => !own.some((item) => item.id === method.id))
      .map((method) => ({ ...method, payerLabel: billToContactName?.trim() || 'Bill to' }));
    return [...own, ...billTo];
  }, [paymentMethodsQuery.data, billToMethodsQuery.data, billToPayerId, billToContactName]);
  const savedMethodsLoading =
    paymentMethodsQuery.isLoading || (Boolean(billToPayerId) && billToMethodsQuery.isLoading);
  // What the server gates a saved-method charge on: the tenant's enabled
  // processors and its card/ACH switches. A saved method's `provider` is fixed
  // at tokenization, so a tenant that moved to Payabli still lists Stripe cards
  // it can no longer charge.
  //
  // Undefined while the settings request is in flight or after it failed, and
  // then NOTHING is chargeable. The server fills in absent processor flags
  // before responding, so a loaded response always carries a real answer;
  // reading "no response yet" as "all enabled" is what let an operator post a
  // charge the server was always going to refuse.
  const chargeSettings: SavedMethodChargeSettings | undefined =
    billingSettingsQuery.data?.paymentMethods;
  const billingSettingsLoading = billingSettingsQuery.isLoading;
  const chargeableMethods = useMemo(
    () =>
      chargeSettings
        ? savedMethods.filter((item) => isSavedMethodChargeable(item, chargeSettings))
        : [],
    [savedMethods, chargeSettings],
  );
  const creditAvailable = Number(
    contactSummaryQuery.data?.paymentCreditAvailable ??
      contactSummaryQuery.data?.creditAvailable ??
      0,
  );
  // Applying credit needs a real balance to clamp against; with the balance
  // hidden there is nothing to compute a ceiling from, so this stays 0 and the
  // "Use credit" control (gated on `maxCreditApply > 0`) withholds itself.
  const rawCreditApply = balanceDueKnown ? Math.min(creditAvailable, balanceDue) : 0;
  // Applying credit posts to a payments:edit route, so a create-only collector
  // must not be offered it at all rather than shown it and refused.
  const maxCreditApply = canEditPayments ? rawCreditApply : 0;
  // Manual is always offered; the other two are conditional, and the toggle row
  // sizes itself to however many actually render.
  const modeChoiceCount =
    1 + (canEditPayments ? 1 : 0) + (maxCreditApply > 0 ? 1 : 0) + (scanSettingOn ? 1 : 0);
  const selectedSavedMethod = savedMethods.find((item) => item.id === savedPaymentMethodId);
  const selectedSavedMethodProvider = selectedSavedMethod?.provider?.trim().toLowerCase();
  // Belt to the picker's braces: the unchargeable options are disabled, but a
  // selection can survive a refetch that changed the settings under it.
  const selectedUnchargeableReason =
    selectedSavedMethod && chargeSettings
      ? savedMethodUnchargeableReason(selectedSavedMethod, chargeSettings)
      : null;
  // The one test both the Charge button and the submit handler apply, so the
  // two can never disagree about whether this charge may go out.
  const selectedSavedMethodChargeable = chargeableMethods.some(
    (item) => item.id === savedPaymentMethodId,
  );
  // Done from the operator's side: the card was charged, or the bank took the
  // debit. A bank debit is PENDING for days by design, so waiting for SUCCEEDED
  // left the dialog open on a charge that had already gone out.
  const savedChargeAccepted =
    chargeSavedPayment.isSuccess &&
    (chargeSavedPayment.data?.status === 'SUCCEEDED' ||
      isAcceptedBankDebit(
        chargeSavedPayment.data,
        chargeSavedPayment.variables?.paymentMethodType,
      ));
  // Any charge the server answered without failing (a decline throws) went out.
  // This dialog sends no second one: an accepted charge closes it, and one still
  // being verified stays on screen with the charge button locked.
  const savedChargeDispatched = chargeSavedPayment.isSuccess;
  const chargeInFlight = newMethodChargeSubmittingState || chargeNewMethodPayment.isPending;
  // A charge that went out of this dialog — or is going out right now — locks
  // the WHOLE dialog: mode buttons, the method picker and the footer.
  // Switching to Cash mid-charge and recording it would credit the invoice a
  // second time for money that may still land. A scanned check being
  // deposited, or whose debit went out, locks it the same way.
  const dialogLocked =
    savedChargeDispatched ||
    chargeInFlight ||
    newMethodChargeUnconfirmed ||
    scanLocked ||
    // A debit covering the balance landed while this dialog was open. Every
    // mode this flag reaches is locked, not just the gateway ones: switching
    // to Cash and recording it credits the invoice a second time for money
    // already on its way. Goes again on its own when the field does — settled
    // or returned. The two takers this flag does NOT reach — the new-method
    // Charge button and the check-scan Deposit button — read `clearingReason`
    // directly where they are rendered; see those two call sites.
    clearingReason !== null;

  // Query invalidation can refresh the invoice (and its balance) before the
  // per-call mutate callback runs. Close from mutation state as well so a
  // successful payment never leaves a stale zero-balance dialog on screen.
  useEffect(() => {
    if (!open || (!recordPayment.isSuccess && !savedChargeAccepted && !applyCredit.isSuccess))
      return;
    onClose();
    recordPayment.reset();
    chargeSavedPayment.reset();
    applyCredit.reset();
  }, [
    applyCredit.isSuccess,
    applyCredit.reset,
    savedChargeAccepted,
    chargeSavedPayment.reset,
    onClose,
    open,
    recordPayment.isSuccess,
    recordPayment.reset,
  ]);

  useEffect(() => {
    if (!open) {
      paymentSession.current = null;
      return;
    }
    const sessionKey = `${invoiceId}:${contactId ?? ''}:${balanceDueKnown}:${canEditPayments}`;
    // Refreshing the balance or loading saved methods must not overwrite an
    // amount/reference already entered, or switch how that payment is recorded.
    if (paymentSession.current !== sessionKey) {
      paymentSession.current = sessionKey;
      modeSettled.current = false;
      setAmount(balanceDueKnown ? balanceDue : '');
      setTip('');
      setMethod('CASH');
      setTransactionId('');
      setOverpaymentConfirmOpen(false);
      setSavedPaymentMethodId('');
      setMode('manual');
      // A fresh dialog session must never replay a key minted for a previous
      // one, even for the same rail/amount by coincidence.
      newMethodChargeAttemptRef.current = null;
      setNewMethodChargeUnconfirmed(false);
      setScanSubmitting(false);
      setScanDebitDispatched(false);
    }
    // The dialog mounts with its open, so on a first open both requests are
    // still in flight here, and until both answer nothing counts as chargeable.
    // Choosing now would pick manual for a contact with a perfectly good card
    // and never look again. Until both are in, stay on manual with nothing
    // selected.
    if (savedMethodsLoading || billingSettingsLoading) return;
    const nextDefaultId = preferredSavedMethodId(chargeableMethods);
    if (!modeSettled.current) {
      modeSettled.current = true;
      setSavedPaymentMethodId(nextDefaultId);
      // Open on saved only when there is something here that can be charged;
      // otherwise manual, exactly as with no saved methods at all. Switching to
      // saved then explains why, rather than the dialog opening on a dead tab.
      setMode(canEditPayments && chargeableMethods.length > 0 ? 'saved' : 'manual');
    } else if (nextDefaultId) {
      setSavedPaymentMethodId((current) => current || nextDefaultId);
    }
  }, [
    open,
    invoiceId,
    contactId,
    balanceDue,
    balanceDueKnown,
    chargeableMethods,
    canEditPayments,
    savedMethodsLoading,
    billingSettingsLoading,
  ]);

  const parsedAmount = amount === '' ? 0 : amount;
  // NOT-535 — gratuity on top of the amount applied to the invoice. Manual-record
  // only (saved-method gateway charges don't collect a tip here).
  const parsedTip = tip === '' ? 0 : tip;
  // The manual method picker's `new:card` / `new:ach` pseudo-choice, when
  // active — renders `NewPaymentMethodCharge` in place of Reference/Tip and
  // hides this dialog's own submit button (that collector's is the only one).
  const selectedNewMethodRail = mode === 'manual' ? parseNewMethodRail(method) : null;
  // Manual payments may exceed the balance due: the backend applies the balance
  // and records the excess as customer credit (overpayment). Gateway charges —
  // a saved method, or a card/bank account entered here — are rejected
  // server-side above the balance, so cap those; a new method would otherwise
  // be saved before the charge is refused. With the balance hidden there is no
  // threshold to exceed, so an overpayment cannot be detected client-side —
  // every amount would otherwise read as one against a stripped 0. The server
  // still applies the balance and books any excess as credit.
  const isGatewayCharge = mode === 'saved' || Boolean(selectedNewMethodRail);
  const isOverpayment =
    balanceDueKnown &&
    mode === 'manual' &&
    !selectedNewMethodRail &&
    parsedAmount > 0 &&
    parsedAmount > balanceDue;
  const overpaymentCredit = isOverpayment ? parsedAmount - balanceDue : 0;
  const amountError =
    balanceDueKnown && parsedAmount > 0 && isGatewayCharge && parsedAmount > balanceDue
      ? `Payment amount cannot exceed balance due (${fmt(balanceDue)})`
      : parsedAmount > 0 && mode === 'credit' && parsedAmount > maxCreditApply
        ? `Credit amount cannot exceed ${fmt(maxCreditApply)}`
        : null;
  // Suppressed when nothing here is chargeable: the picker already carries the
  // full explanation, and "please select one" would be advice the operator
  // cannot take.
  const savedMethodError =
    mode !== 'saved' || chargeableMethods.length === 0
      ? null
      : !savedPaymentMethodId || !selectedSavedMethodProvider
        ? 'Please select a saved payment method'
        : // The server would answer this with a 400 after the operator committed.
          // Say it here instead — the charge itself is unchanged.
          selectedUnchargeableReason
          ? `This payment method cannot be charged — ${selectedUnchargeableReason}.`
          : null;

  // The card service fee this charge will add, for THIS instrument and THIS
  // amount. Quoted by the server (pre-tax proration + network cap + cost-of-
  // acceptance cap), never multiplied out here, so the operator can state the
  // figure the customer's statement will show. Saved-method charges only — a
  // manually recorded card payment is money that was taken elsewhere, and we
  // add nothing to it.
  // Debounced so typing an amount does not fire a request per keystroke — and
  // the disclosure renders its total off the SAME debounced figure, so the fee
  // and the total it is added to always describe one amount.
  const quotedAmount = useDebounce(parsedAmount, 300);
  const serviceFeeQuote = useCardServiceFeeQuote({
    amount: quotedAmount,
    paymentMethodType: selectedSavedMethod?.type ?? null,
    invoiceId,
    provider: selectedSavedMethodProvider ?? null,
    enabled: open && mode === 'saved' && Boolean(selectedSavedMethod),
  });
  // While the debounced amount trails what was typed — or its quote is still in
  // flight — the disclosure on screen describes a DIFFERENT charge than the one
  // the button would send. Suppress the stale figure and refuse the charge
  // until they agree: "quoted fee = charged fee" has to hold at the click, not
  // just eventually.
  const serviceFeeQuoteStale =
    mode === 'saved' &&
    Boolean(selectedSavedMethod) &&
    parsedAmount > 0 &&
    (quotedAmount !== parsedAmount || serviceFeeQuote.isFetching);

  // Staff charging a saved bank account by hand is a one-off debit: it needs
  // the customer's authorization for THIS debit, recorded the same way as for
  // a bank account just typed in. The server refuses the charge without it.
  // Named for the payer — the bill-to contact when the method is theirs.
  const savedBankCharge =
    mode === 'saved' &&
    selectedSavedMethod !== undefined &&
    isBankPaymentMethod(selectedSavedMethod);
  // A bill-to payer's own record: the authorization names them, and its copy
  // goes to their email (the server sends it to the payer contact), so both
  // come from that contact rather than the invoice's snapshot.
  const savedBankForBillTo = savedBankCharge && Boolean(selectedSavedMethod?.payerLabel);
  const billToPayerQuery = useContact(billToPayerId ?? '', {
    enabled: open && savedBankForBillTo,
  });
  const billToPayer = billToPayerQuery.data;
  const savedMethodPayerName = savedBankForBillTo
    ? billToPayer
      ? getContactDisplayName(billToPayer)
      : ''
    : contactName;
  const savedMethodPayerEmail = savedBankForBillTo
    ? (billToPayer?.primaryEmail ?? null)
    : contactEmail;
  // What leaves the account: the base after the tenant's ACH/check discount,
  // as the server quoted it. Unknown until that quote describes this amount.
  const savedBankDebitAmount =
    parsedAmount > 0 && !serviceFeeQuoteStale && serviceFeeQuote.data
      ? serviceFeeQuote.data.achCheckDiscountEnabled &&
        typeof serviceFeeQuote.data.chargeCents === 'number'
        ? serviceFeeQuote.data.chargeCents / 100
        : parsedAmount
      : null;
  const savedAchAuthorization = useAchAuthorization({
    contactName: savedMethodPayerName,
    debitAmount: savedBankDebitAmount,
    enabled: savedBankCharge,
    amountEntered: parsedAmount > 0,
    accountKey: savedBankCharge ? savedPaymentMethodId : null,
    payerNameLoading: savedBankForBillTo && billToPayerQuery.isLoading,
  });
  const resetSavedAchAuthorization = savedAchAuthorization.reset;
  // Each dialog session takes its own authorization, dated the day it opens.
  // The dialog stays mounted with the invoice page, so this runs on open.
  useEffect(() => {
    if (open) resetSavedAchAuthorization();
  }, [open, resetSavedAchAuthorization]);

  const manualDiscount = computePaymentMethodDiscount(
    billingSettingsQuery.data?.paymentMethods,
    Math.max(0, Math.round(parsedAmount * 100)),
    mode === 'manual' && !selectedNewMethodRail && balanceDueKnown && parsedAmount <= balanceDue
      ? method
      : '',
  );

  /**
   * `NewPaymentMethodCharge`'s `onMethodReady` — it has already saved the
   * card/bank account the operator just typed and hands back its id here to
   * actually charge it against this invoice. The collector's own submitting
   * lock stays on until this promise settles, and clears it ONLY on
   * rejection, so a refused charge must both surface the error AND rethrow
   * (never swallow it) or the collector would be stuck locked with no way to
   * retry. Mirrors the contact dialog's `chargeNewMethod` exactly, less the
   * apply-to-invoices checkbox — this charge is already bound to `invoiceId`.
   */
  async function chargeNewMethod({
    paymentMethodId,
    providerPaymentMethodId,
    removeAfterCharge,
    authorization,
    chargedAmount,
  }: {
    paymentMethodId: string;
    /** The saved method's provider-side id — what a replayed charge is told apart by. */
    providerPaymentMethodId?: string | null;
    removeAfterCharge: boolean;
    authorization?: StaffChargeAuthorization;
    /** What the collector says the customer is debited before any card fee
     * — after an ACH/check discount — named in the success copy. */
    chargedAmount: number;
  }) {
    const amount = parsedAmount;
    const rail = selectedNewMethodRail ?? 'card';
    const previousAttempt = newMethodChargeAttemptRef.current;
    const reusesKey =
      !!previousAttempt && previousAttempt.rail === rail && previousAttempt.amount === amount;
    const idempotencyKey = reusesKey ? previousAttempt.key : createUuid();
    const sentEarlierUnderKey: ReadonlySet<string> = reusesKey
      ? previousAttempt.providerIds
      : new Set();
    newMethodChargeAttemptRef.current = {
      key: idempotencyKey,
      rail,
      amount,
      providerIds: providerPaymentMethodId
        ? new Set([...sentEarlierUnderKey, providerPaymentMethodId])
        : sentEarlierUnderKey,
    };
    const sentBefore = newMethodSentIdsRef.current.has(paymentMethodId);
    newMethodSentIdsRef.current.add(paymentMethodId);
    // Best-effort, and never masks the charge's own outcome.
    const removeOneTimeMethod = async (reason: string) => {
      if (!contactId) return;
      try {
        await deletePaymentMethod.mutateAsync(paymentMethodId);
      } catch (removalError) {
        console.error(
          `[RecordPaymentDialog] failed to remove a one-time payment method after ${reason}`,
          removalError,
        );
      }
    };
    newMethodChargeSubmitting.current = true;
    setNewMethodChargeSubmittingState(true);

    try {
      const result = await chargeNewMethodPayment.mutateAsync({
        invoiceId,
        amount,
        savedPaymentMethodId: paymentMethodId,
        paymentMethodType: rail === 'ach' ? 'ACH' : 'CARD',
        // Same shape as the contact dialog's collector. `newlyEnteredMethod`
        // is what scopes the server's bank-authorization requirement to this
        // collector; `removeAfterCharge` is only the one-time-use option.
        removeAfterCharge: removeAfterCharge || undefined,
        newlyEnteredMethod: true,
        achAuthorization: authorization,
        idempotencyKey,
      });

      // Definitive: never replay a charge the server answered.
      newMethodChargeAttemptRef.current = null;

      const status = typeof result?.status === 'string' ? result.status : undefined;
      // After an ambiguous failure the held key names the EARLIER attempt:
      // the route returns that attempt's payment, on the method entered then,
      // and never sends this one to the processor. Report that payment as it
      // stands.
      if (
        !sentBefore &&
        replayedEarlierMethod(result, providerPaymentMethodId, sentEarlierUnderKey)
      ) {
        if (removeAfterCharge) {
          await removeOneTimeMethod('a replayed charge');
        }
        const replay = describeReplayedCharge(result, rail, chargedAmount, fmt);
        if (replay.settled) toast.success(replay.message);
        else toast.info(replay.message);
        trackAction('invoice_new_payment_method_charged', {
          invoiceId,
          amount,
          rail,
          status: replay.status,
          replayed: true,
        });
        onClose();
        return;
      }
      // A bank debit is PENDING for days by design: accepted, and done from
      // the operator's side. A card that comes back PENDING is not — it is
      // handled like the saved-method flow's own unconfirmed charge: the
      // dialog stays open on its status copy, locked.
      const bankDebitAccepted = rail === 'ach' && status === 'PENDING';
      if (status !== 'SUCCEEDED' && !bankDebitAccepted) {
        setNewMethodChargeUnconfirmed(true);
        toast.info(
          status === 'PENDING'
            ? 'Payment is pending. Check the invoice Payments tab for its status.'
            : 'Payment is not confirmed. Check the invoice Payments tab for its status.',
        );
        return;
      }

      toast.success(
        bankDebitAccepted
          ? `Bank payment of ${fmt(chargedAmount)} submitted — clears in 3–5 business days.`
          : `Payment of ${fmt(chargedAmount)} charged.`,
      );
      trackAction('invoice_new_payment_method_charged', { invoiceId, amount, rail, status });
      onClose();
    } catch (error) {
      // Same classification every new-method-charge collector uses (shared
      // with the contact dialog) — kept for an exact retry because the
      // server never gave a definitive answer.
      const { canRetryWithSameKey, isAmbiguousOutcome } = classifyNewMethodChargeFailure(error);
      if (!canRetryWithSameKey) {
        newMethodChargeAttemptRef.current = null;
      }

      if (isAmbiguousOutcome) {
        toast.error(AMBIGUOUS_NEW_METHOD_CHARGE_MESSAGE);
      } else if (error instanceof ApiError) {
        // Mirrors `useApiMutation`'s own built-in error toast exactly (the
        // one the pre-existing "Charge Saved Method" flow above still relies
        // on) — this mutation's is suppressed (`showErrorToast: false`) so it
        // doesn't stack with the ambiguous-outcome copy above.
        const fieldErrors = error.fieldErrors;
        if (fieldErrors && fieldErrors.length > 0) {
          toastApiError(error, validationToastMessage(fieldErrors));
        } else if (error.code !== 'CANCELED') {
          toastApiError(error);
        }
      }

      // A method saved for one-time use only comes off the contact when it is
      // known not to have been charged: on a definitive refusal, or when an
      // answer about an EARLIER, unresolved attempt proves this request
      // charged nothing (a 409 raised before submission). Never on an
      // ambiguous outcome for THIS method, where the charge may have gone
      // through and removing the method would strand it with no way to look
      // the charge up by its instrument.
      const provenNotCharged = !sentBefore && chargeRefusedBeforeCharging(error);
      if (removeAfterCharge && (!isAmbiguousOutcome || provenNotCharged)) {
        await removeOneTimeMethod('a refused charge');
      }
      throw error;
    } finally {
      newMethodChargeSubmitting.current = false;
      setNewMethodChargeSubmittingState(false);
    }
  }

  function closeUnlessScanning() {
    if (scanSubmitting) return;
    onClose();
  }

  function handleRecord() {
    if (!parsedAmount || parsedAmount <= 0 || amountError || savedMethodError) return;
    // A charge that went out of this dialog locks the WHOLE dialog, not just the
    // saved-method button. A non-accepted answer — a PENDING card, say — leaves
    // the dialog open on purpose so the operator can read the status copy, and
    // switching to `Record manually` or `Use credit` from there would credit the
    // invoice a second time for money that may still land. The mode buttons are
    // disabled for the same reason; this is the click that beats them. A
    // new-method charge still in flight counts the same way.
    if (dialogLocked || newMethodChargeSubmitting.current) return;
    // Refuse rather than charge an amount whose fee has not been quoted yet —
    // the disabled button is the visible half of this, but the overpayment
    // confirm dialog reaches here by another route.
    if (serviceFeeQuoteStale) return;
    // New-method charges submit through `chargeNewMethod` via
    // `NewPaymentMethodCharge`'s own button, never this dialog's footer one.
    if (mode === 'manual' && selectedNewMethodRail) return;

    if (mode === 'credit') {
      if (!contactId || maxCreditApply <= 0) return;
      applyCredit.mutate(
        { contactId, invoiceId, amount: parsedAmount },
        {
          onSuccess: () => {
            trackAction('invoice_payment_credit_applied', {
              invoiceId,
              contactId,
              amount: parsedAmount,
            });
            onClose();
          },
        },
      );
      return;
    }

    if (mode === 'saved') {
      // Same condition `savedMethodError` already refuses, restated inside the
      // branch so it narrows: as one arm of the disjunction above, the compiler
      // cannot tie it back to this `mode` check and still reads the provider as
      // possibly undefined at the call. `savedMethodError` is suppressed when
      // nothing is chargeable, so chargeability is restated here too.
      if (!selectedSavedMethodProvider || !selectedSavedMethodChargeable) return;
      // One charge per dialog: never while one is in flight, never after one
      // went out. The disabled button says the same; this is the half a second
      // click inside the same tick cannot get past.
      if (savedChargeSubmitting.current || chargeSavedPayment.isPending || savedChargeDispatched)
        return;
      savedChargeSubmitting.current = true;

      const paymentMethodType = selectedSavedMethod?.type;
      chargeSavedPayment.mutate(
        {
          invoiceId,
          amount: parsedAmount,
          savedPaymentMethodId,
          provider: selectedSavedMethodProvider,
          paymentMethodType,
          staffInitiated: true,
          achAuthorization: savedAchAuthorization.build(),
        },
        {
          onSuccess: (payment) => {
            const accepted =
              payment.status === 'SUCCEEDED' || isAcceptedBankDebit(payment, paymentMethodType);
            if (!accepted) return;
            trackAction('invoice_saved_payment_method_charged', {
              invoiceId,
              amount: parsedAmount,
              savedPaymentMethodId,
              status: payment.status,
            });
            onClose();
          },
          // A decline or lost response leaves nothing dispatched from here, so
          // the operator may try again; an answered charge stays locked above.
          onSettled: () => {
            savedChargeSubmitting.current = false;
          },
        },
      );
      return;
    }

    recordPayment.mutate(
      {
        invoiceId,
        amount: manualDiscount.chargeCents / 100,
        ...(manualDiscount.discountCents > 0
          ? { paymentMethodDiscountGrossAmount: parsedAmount }
          : {}),
        // Guarded above: `handleRecord` returns early while a new-method
        // rail is selected, so `method` is always a real recorded method here.
        paymentMethod: method as PaymentMethod,
        ...(transactionId ? { transactionId } : {}),
        ...(parsedTip > 0 ? { tipAmount: parsedTip } : {}),
      },
      {
        onSuccess: () => {
          trackAction('invoice_payment_recorded', {
            invoiceId,
            amount: parsedAmount,
            method,
            tip: parsedTip,
          });
          onClose();
        },
      },
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && closeUnlessScanning()}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {balanceDueKnown
                ? `Balance due: ${fmt(balanceDue)}`
                : 'Enter the amount collected from the customer.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div
              className={`grid grid-cols-1 gap-2 ${
                modeChoiceCount >= 3
                  ? 'sm:grid-cols-3'
                  : modeChoiceCount === 2
                    ? 'sm:grid-cols-2'
                    : 'sm:grid-cols-1'
              }`}
            >
              <Button
                type="button"
                variant={mode === 'manual' ? 'default' : 'outline'}
                onClick={() => {
                  modeSettled.current = true;
                  if (mode === 'credit') setAmount(balanceDue);
                  setMode('manual');
                }}
                // Locked once a charge has gone out of this dialog (or while
                // one is going out): the other modes would collect again over
                // a payment that may still land.
                disabled={dialogLocked}
              >
                Record manually
              </Button>
              {canEditPayments ? (
                <Button
                  type="button"
                  variant={mode === 'saved' ? 'default' : 'outline'}
                  onClick={() => {
                    modeSettled.current = true;
                    if (mode === 'credit') setAmount(balanceDue);
                    setMode('saved');
                  }}
                  disabled={!contactId || dialogLocked}
                >
                  Charge saved method
                </Button>
              ) : null}
              {maxCreditApply > 0 ? (
                <Button
                  type="button"
                  variant={mode === 'credit' ? 'default' : 'outline'}
                  onClick={() => {
                    modeSettled.current = true;
                    setMode('credit');
                    setAmount(maxCreditApply);
                  }}
                  disabled={dialogLocked}
                >
                  <ArrowRightLeft className="mr-1.5 size-3.5" />
                  Use credit
                </Button>
              ) : null}
              {scanSettingOn ? (
                <Button
                  type="button"
                  variant={mode === 'scan' ? 'default' : 'outline'}
                  onClick={() => {
                    modeSettled.current = true;
                    if (mode === 'credit') setAmount(balanceDue);
                    setMode('scan');
                  }}
                  // A new-method charge that is out or unresolved locks this too:
                  // a check deposited over it would collect twice.
                  disabled={!scanAvailable || dialogLocked}
                  title={scanUnavailableReason ?? undefined}
                >
                  <ScanLine className="mr-1.5 size-3.5" />
                  Scan check
                </Button>
              ) : null}
            </div>
            {scanUnavailableReason ? (
              <p className="text-xs text-muted-foreground" data-testid="check-scan-unavailable">
                Check scanning is unavailable: {scanUnavailableReason}
              </p>
            ) : null}
            {mode === 'scan' ? (
              // The check is deposited for what is written on it — staff enter
              // that into the scanner, and Payabli checks it against the paper.
              <p className="text-sm text-muted-foreground">
                Personal checks only, with the customer here in person. The check is deposited for
                the amount written on it.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="min-w-0 space-y-1.5">
                  <label className="text-sm font-medium">
                    {manualDiscount.enabled ? 'Amount before payment discount' : 'Amount'}
                  </label>
                  <CurrencyInput
                    value={amount}
                    onChange={(value) => {
                      modeSettled.current = true;
                      setAmount(value);
                    }}
                  />
                  {manualDiscount.discountCents > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Record {fmt(manualDiscount.chargeCents / 100)} received, plus{' '}
                      {fmt(manualDiscount.discountCents / 100)} ACH/check discount.{' '}
                      {parsedTip > 0 ? 'Gratuity is added separately.' : ''}
                    </p>
                  )}
                  {amountError && <p className="text-xs text-destructive">{amountError}</p>}
                  {isOverpayment && (
                    <p className="flex items-start gap-1 text-xs font-medium text-amber-600 dark:text-amber-500">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                      <span>
                        Overpayment: {fmt(balanceDue)} applies to this invoice and{' '}
                        {fmt(overpaymentCredit)} will be added as customer credit.
                      </span>
                    </p>
                  )}
                </div>
                <div className="min-w-0 space-y-1.5">
                  {mode === 'manual' ? (
                    <>
                      <label className="text-sm font-medium">Payment Method</label>
                      <Select
                        value={method}
                        onValueChange={(v) => {
                          modeSettled.current = true;
                          setMethod(v as ManualMethodChoice);
                        }}
                        // Switching away from a card mid-charge to Cash would
                        // bring back this dialog's own Record Payment button.
                        disabled={dialogLocked}
                      >
                        <SelectTrigger className="w-full min-w-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          className="w-[var(--radix-select-trigger-width)]"
                        >
                          {PAYMENT_METHODS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                          {/* Shown only once the contact and its chargeable
                              rails are both known — never a rail the collector
                              itself would refuse to mint. */}
                          {contactId && newMethodRailAvailability.card ? (
                            <SelectItem value={newMethodValue('card')}>Card</SelectItem>
                          ) : null}
                          {contactId && newMethodRailAvailability.ach ? (
                            <SelectItem value={newMethodValue('ach')}>
                              Bank account (ACH)
                            </SelectItem>
                          ) : null}
                        </SelectContent>
                      </Select>
                    </>
                  ) : mode === 'saved' ? (
                    <>
                      <label className="text-sm font-medium">Saved Payment Method</label>
                      <Select
                        value={savedPaymentMethodId}
                        onValueChange={(value) => setSavedPaymentMethodId(value)}
                      >
                        <SelectTrigger className="w-full min-w-0">
                          <SelectValue placeholder="Select a saved payment method" />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          className="w-[var(--radix-select-trigger-width)]"
                        >
                          {savedMethods.map((item) => {
                            // Same formatter the contact detail page uses, so the
                            // card an operator picks here reads as the card they
                            // looked at there. Dropping `label` and `type` turned
                            // four distinct instruments into two identical-looking
                            // pairs — and on a surcharging tenant credit vs debit
                            // is a different price to the customer.
                            const base = formatSavedMethodOptionLabel(item);
                            const withPayer = item.payerLabel
                              ? `${base} · Bill to: ${item.payerLabel}`
                              : base;
                            // Listed but not selectable: hiding it would leave the
                            // operator hunting for a card they can see on the
                            // contact, with no hint that it needs re-adding.
                            // Without settings every option is disabled; the
                            // message under the picker says why.
                            const unchargeable = chargeSettings
                              ? savedMethodUnchargeableReason(item, chargeSettings)
                              : null;
                            return (
                              <SelectItem
                                key={item.id}
                                value={item.id}
                                disabled={!chargeSettings || !!unchargeable}
                              >
                                {unchargeable ? `${withPayer} — ${unchargeable}` : withPayer}
                                {/* What the processor thinks of this instrument,
                                  which is a different question from whether
                                  this tenant may charge it at all. Not folded
                                  into `unchargeable`: that mirrors the server's
                                  gates, and the server does not refuse a
                                  declined method on an operator-initiated
                                  charge. Shown, not blocked. */}
                                <PaymentMethodHealthBadge
                                  state={item.declineState}
                                  className="ml-1"
                                />
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {savedMethodError && (
                        <p className="text-xs text-destructive">{savedMethodError}</p>
                      )}
                    </>
                  ) : (
                    <div className="rounded-md border bg-muted/30 px-3 py-2">
                      <p className="text-xs text-muted-foreground">Available customer credit</p>
                      <p className="font-semibold tabular-nums">{fmt(creditAvailable)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {mode === 'manual' && selectedNewMethodRail && contactId ? (
              <NewPaymentMethodCharge
                rail={selectedNewMethodRail}
                amount={parsedAmount}
                contactId={contactId}
                contactName={contactName}
                contactEmail={contactEmail}
                existingMethodIds={paymentMethodsQuery.data?.map((item) => item.id) ?? []}
                onMethodReady={chargeNewMethod}
                // Capped at the balance like a saved-method charge: the server
                // refuses more, and only after this method has been saved.
                // A scanned check being deposited (or whose debit went out)
                // locks this rail too: the check may still land.
                // And until the contact's saved methods are known, a method
                // the server deduplicated onto an existing one would read as
                // new, and a one-time charge would remove the customer's card.
                // `clearingReason` explicitly, NOT `dialogLocked`: this is the
                // only taker in new-method mode — the footer submit is not
                // rendered for it at all — so without this the operator reads
                // "covers this balance" directly under an enabled Charge
                // button. The rest of `dialogLocked` is this component's own
                // charge state, and disabling it on that would fight its
                // internal guards.
                disabled={
                  !(parsedAmount > 0) ||
                  !!amountError ||
                  scanLocked ||
                  clearingReason !== null ||
                  !paymentMethodsQuery.data
                }
              />
            ) : mode === 'manual' ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {tipsEnabled ? (
                    <div className="min-w-0 space-y-1.5">
                      <label className="text-sm font-medium">
                        Tip / Gratuity{' '}
                        <span className="text-xs text-muted-foreground font-normal">
                          (optional)
                        </span>
                      </label>
                      <CurrencyInput
                        value={tip}
                        onChange={(value) => {
                          modeSettled.current = true;
                          setTip(value);
                        }}
                      />
                    </div>
                  ) : null}
                  <div className="min-w-0 space-y-1.5">
                    <label className="text-sm font-medium">
                      Reference Number{' '}
                      <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <Input
                      value={transactionId}
                      onChange={(e) => {
                        modeSettled.current = true;
                        setTransactionId(e.target.value);
                      }}
                      placeholder="Check #, transaction ID, etc."
                    />
                  </div>
                </div>
                {parsedTip > 0 ? (
                  <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">
                      Total collected{' '}
                      <span className="text-xs">
                        ({fmt(manualDiscount.chargeCents / 100)} + {fmt(parsedTip)} tip)
                      </span>
                    </span>
                    <span className="font-semibold tabular-nums">
                      {fmt(manualDiscount.chargeCents / 100 + parsedTip)}
                    </span>
                  </div>
                ) : null}
              </>
            ) : mode === 'scan' ? (
              // Mounted while a deposit is in flight whatever the availability
              // read says since: unmounting mid-debit loses the confirm.
              scanFormConfig ? (
                <PayabliCheckScanForm
                  invoiceId={invoiceId}
                  balanceDue={balanceDueKnown ? balanceDue : null}
                  discountSettings={billingSettingsQuery.data?.paymentMethods}
                  contactId={contactId ?? null}
                  config={scanFormConfig}
                  // Entering scan mode is already refused while a debit
                  // covers the balance (the mode button reads `dialogLocked`);
                  // this is the operator who was ALREADY scanning when it
                  // landed. Nothing has moved yet at this point — the form's
                  // own `debitDispatched` covers the case where it has — so
                  // stopping here strands no money and leaves Record manually
                  // and Cancel open.
                  blockedReason={clearingReason}
                  onSubmittingChange={setScanSubmitting}
                  onDebitDispatchedChange={setScanDebitDispatched}
                  onCancel={onClose}
                  onRecordManually={() => {
                    modeSettled.current = true;
                    setMode('manual');
                    setMethod('CHECK');
                  }}
                  onDone={(outcome) => {
                    trackAction('invoice_check_scanned', {
                      invoiceId,
                      amount: outcome.amount,
                      status: outcome.status,
                    });
                    // The invoice, its payments, receipts and the lists were
                    // refreshed when the deposit was confirmed
                    // (`invalidateAfterChequeScan`).
                    onClose();
                  }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Check scanning is unavailable right now.
                </p>
              )
            ) : mode === 'credit' ? (
              <p className="text-sm text-muted-foreground">
                Apply up to {fmt(maxCreditApply)} from this customer's existing payment credit to
                this invoice. No new payment will be collected.
              </p>
            ) : savedMethodsLoading ? (
              <p className="text-sm text-muted-foreground">Loading saved payment methods...</p>
            ) : savedMethods.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {billToPayerId
                  ? `Neither this contact nor the bill-to contact (${billToContactName?.trim() || 'Bill to'}) has any saved payment methods yet. Add one from their payment methods screen first.`
                  : 'This contact does not have any saved payment methods yet. Add one from the contact payment methods screen first.'}
              </p>
            ) : billingSettingsLoading ? (
              <p className="text-sm text-muted-foreground">Loading saved payment methods...</p>
            ) : !chargeSettings ? (
              // Failed with no cached answer. Nothing here can be vetted, so no
              // charge goes out; recording manually charges nothing and stays open.
              <p role="alert" className="text-sm text-destructive">
                Saved payment methods can't be charged right now: this company's payment settings
                didn't load. Record the payment manually, or close this and try again.
              </p>
            ) : chargeableMethods.length === 0 ? (
              <p role="alert" className="text-sm text-destructive">
                {unchargeableSavedMethodsMessage(savedMethods, chargeSettings)}
              </p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  This charges the selected saved payment method off-session and applies the payment
                  to the invoice.
                </p>
                {serviceFeeQuoteStale ? null : (
                  <ChargeServiceFeePreview
                    quote={serviceFeeQuote.data}
                    baseAmount={quotedAmount}
                    className="mt-2"
                  />
                )}
                {savedBankCharge ? (
                  <div className="mt-2">
                    <AchAuthorizationFields
                      authorization={savedAchAuthorization}
                      disabled={chargeSavedPayment.isPending || dialogLocked}
                      showNoEmailWarning={
                        !(savedBankForBillTo && billToPayerQuery.isLoading) &&
                        !savedMethodPayerEmail
                      }
                    />
                  </div>
                ) : null}
              </>
            )}
            {mode === 'saved' && chargeSavedPayment.error && (
              <p role="alert" className="text-sm text-destructive">
                {chargeSavedPayment.error.message}
              </p>
            )}
            {clearingReason !== null && (
              <p role="status" className="text-sm text-muted-foreground">
                {clearingReason} covers this balance. Collect again only if it is returned or fails.
              </p>
            )}
            {newMethodChargeUnconfirmed && (
              <p role="status" className="text-sm text-muted-foreground">
                Payment is pending. It has not been applied to this invoice yet. Check the invoice
                Payments tab for its status before collecting another payment.
              </p>
            )}
            {mode === 'saved' && chargeSavedPayment.data?.status === 'PENDING' && (
              <p role="status" className="text-sm text-muted-foreground">
                Payment is pending. It has not been applied to this invoice yet. Check the invoice
                Payments tab for its status before collecting another payment.
              </p>
            )}
          </div>
          {mode === 'scan' ? null : (
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {/* A new-method charge submits through `NewPaymentMethodCharge`'s
                  own "Charge $X" button — this dialog's submit button would be
                  a second, conflicting way to send the same charge. */}
              {mode === 'manual' && selectedNewMethodRail ? null : (
                <Button
                  onClick={() => (isOverpayment ? setOverpaymentConfirmOpen(true) : handleRecord())}
                  disabled={
                    recordPayment.isPending ||
                    chargeSavedPayment.isPending ||
                    applyCredit.isPending ||
                    !parsedAmount ||
                    parsedAmount <= 0 ||
                    !!amountError ||
                    !!savedMethodError ||
                    serviceFeeQuoteStale ||
                    (mode === 'credit' && (!contactId || maxCreditApply <= 0)) ||
                    (mode === 'saved' && !selectedSavedMethodChargeable) ||
                    (savedBankCharge && !savedAchAuthorization.ready) ||
                    dialogLocked
                  }
                >
                  {(recordPayment.isPending ||
                    chargeSavedPayment.isPending ||
                    applyCredit.isPending) && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                  {mode === 'saved'
                    ? 'Charge Saved Method'
                    : mode === 'credit'
                      ? 'Apply Credit'
                      : 'Record Payment'}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* NOT-517: a manual overpayment silently created a large customer credit;
          require an explicit confirmation before allocating it. */}
      <AlertDialog open={overpaymentConfirmOpen} onOpenChange={setOverpaymentConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm overpayment</AlertDialogTitle>
            <AlertDialogDescription>
              You are recording {fmt(parsedAmount)}, which is {fmt(overpaymentCredit)} more than the{' '}
              {fmt(balanceDue)} balance due. {fmt(balanceDue)} will be applied to this invoice and{' '}
              {fmt(overpaymentCredit)} will be added as customer credit. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setOverpaymentConfirmOpen(false);
                handleRecord();
              }}
            >
              Record overpayment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── PaymentLinkDialog (moved from route) ────────────────────────────────

interface PaymentLinkDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  existingLink?: string;
}

export function PaymentLinkDialog({
  open,
  onClose,
  invoiceId,
  existingLink,
}: PaymentLinkDialogProps) {
  const [link, setLink] = useState(existingLink ?? '');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open && !existingLink) {
      setLoading(true);
      apiPost<GeneratePaymentLinkResponse, Record<string, never>>(
        operationsClient,
        invoiceEndpoints.generatePaymentLink(invoiceId),
        {},
      )
        .then((res) => {
          setLink(res.paymentLink);
          queryClient.setQueryData<Invoice>(invoiceKeys.detail(invoiceId), (old) =>
            old ? { ...old, paymentLink: res.paymentLink } : old,
          );
        })
        .catch(() => setLink(''))
        .finally(() => setLoading(false));
    } else if (open && existingLink) {
      setLink(existingLink);
    }
  }, [open, invoiceId, existingLink]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCopy() {
    if (!link) return;
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Online Payment Link</DialogTitle>
          <DialogDescription>
            Share this link with your customer to accept online payment.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 size={14} className="animate-spin" />
              Generating link…
            </div>
          ) : link ? (
            <div className="flex items-center gap-2">
              <Input value={link} readOnly className="text-xs font-mono" />
              <Button size="sm" variant="outline" onClick={handleCopy}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Failed to generate link.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
